import React from 'react';
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';

const safeString = (value, fallback = '') => (typeof value === 'string' ? value : fallback);
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const parseTRDate = (value) => {
  const [day, month, year] = safeString(value).split('.').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
const formatTRDate = (date) => date.toLocaleDateString('tr-TR');

const buildCampaignTimelineRows = (campaigns) => {
  const sortedCampaigns = campaigns
    .map((campaign) => ({
      type: 'campaign',
      campaign,
      startDate: parseTRDate(campaign?.start),
      endDate: parseTRDate(campaign?.end)
    }))
    .sort((a, b) => (a.startDate?.getTime() || 0) - (b.startDate?.getTime() || 0));

  return sortedCampaigns.reduce((rows, current, index) => {
    const previous = sortedCampaigns[index - 1];
    if (previous?.endDate && current.startDate) {
      const gapStart = addDays(previous.endDate, 1);
      const gapEnd = addDays(current.startDate, -1);
      const gapDays = Math.round((gapEnd.getTime() - gapStart.getTime()) / MS_PER_DAY) + 1;
      if (gapDays > 0) {
        rows.push({
          type: 'gap',
          id: `gap-${previous.campaign.id}-${current.campaign.id}`,
          start: formatTRDate(gapStart),
          end: formatTRDate(gapEnd),
          days: gapDays
        });
      }
    }
    rows.push(current);
    return rows;
  }, []);
};

function CampaignTable({ campaigns, activeCampaignId = '', onSelectCampaign, onOpenCampaignDetail, onOpenVisualAssets }) {
  const headers = ['Kampanya Adı', 'Tarih Aralığı', 'Kampanya görselleri'];
  const safeCampaigns = Array.isArray(campaigns) ? campaigns : [];
  const timelineRows = buildCampaignTimelineRows(safeCampaigns);

  return (
    <Box
      className="ky-table-root"
      sx={{
        borderRadius: 2,
        border: '1px solid rgba(151,113,39,0.2)',
        background: 'linear-gradient(180deg, #fffaf0 0%, #f7efe0 100%)',
        overflow: 'hidden',
        minHeight: 350
      }}
    >
      <TableContainer className="ky-table-wrap" sx={{ maxWidth: '100%', overflowX: 'auto' }}>
        <Table className="ky-table" stickyHeader size="small" sx={{ minWidth: 700, tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              {headers.map((header, hi) => (
                <TableCell
                  key={header}
                  sx={{
                    backgroundColor: '#efe2c9',
                    color: '#2f2516',
                    fontWeight: 800,
                    fontSize: '0.9rem',
                    px: 0.9,
                    py: 0.86,
                    borderBottom: '1px solid rgba(151,113,39,0.24)',
                    whiteSpace: 'nowrap',
                    width: hi === 0 ? '32%' : hi === 1 ? '28%' : '40%'
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {timelineRows.length ? (
              timelineRows.map((row) => {
                if (row.type === 'gap') {
                  return (
                    <TableRow
                      key={row.id}
                      className="ky-gap-row"
                      sx={{
                        bgcolor: 'rgba(107,114,128,0.08)',
                        '& td': { borderBottom: '1px dashed rgba(107,114,128,0.24)', py: 0.9, px: 1 }
                      }}
                    >
                      <TableCell className="ky-cell-name">
                        <Typography sx={{ color: '#6b5b40', fontSize: '0.9rem', fontWeight: 900 }}>
                          Kampanyasız Dönem
                        </Typography>
                        <Typography sx={{ color: '#8a785f', fontSize: '0.72rem', fontWeight: 700 }}>
                          Bu aralıkta planlı kampanya yok
                        </Typography>
                      </TableCell>
                      <TableCell className="ky-cell-date" sx={{ color: '#6b5b40', fontSize: '0.86rem', whiteSpace: 'nowrap', fontWeight: 800 }}>
                        {row.start} - {row.end} ({row.days} gün)
                      </TableCell>
                      <TableCell className="ky-cell-visuals" onClick={(e) => e.stopPropagation()} />
                    </TableRow>
                  );
                }
                const campaign = row.campaign;
                const isActive = activeCampaignId === campaign.id;
                return (
                  <TableRow
                    key={campaign.id}
                    className="ky-data-row"
                    hover
                    onClick={() => onSelectCampaign(campaign)}
                    onDoubleClick={() => onOpenCampaignDetail(campaign)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: isActive ? 'rgba(151,113,39,0.1)' : 'transparent',
                      boxShadow: isActive ? 'inset 3px 0 0 rgba(151,113,39,0.55)' : 'none',
                      '&:nth-of-type(even)': { bgcolor: isActive ? 'rgba(151,113,39,0.1)' : 'rgba(151,113,39,0.035)' },
                      '&:hover': { bgcolor: 'rgba(151,113,39,0.12)', boxShadow: '0 8px 18px rgba(151,113,39,0.12)' },
                      '& td': { borderBottom: '1px solid rgba(151,113,39,0.1)', py: 1.05, px: 1 }
                    }}
                  >
                    <TableCell className="ky-cell-name">
                      <Typography
                        sx={{
                          color: '#2f2516',
                          fontSize: '0.95rem',
                          fontWeight: 900,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {safeString(campaign.name, 'Yeni Kampanya')}
                      </Typography>
                    </TableCell>
                    <TableCell className="ky-cell-date" sx={{ color: '#5f5140', fontSize: '0.88rem', whiteSpace: 'nowrap' }}>
                      {safeString(campaign.start, '-')} - {safeString(campaign.end, '-')}
                    </TableCell>
                    <TableCell className="ky-cell-visuals" onClick={(e) => e.stopPropagation()} align="left" sx={{ verticalAlign: 'middle' }}>
                      <Button
                        className="ky-visual-btn"
                        size="small"
                        startIcon={<ImageOutlinedIcon sx={{ fontSize: 20 }} />}
                        onClick={() => onOpenVisualAssets && onOpenVisualAssets(campaign)}
                        variant="outlined"
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.82rem',
                          fontWeight: 800,
                          py: 0.4,
                          px: 1.1,
                          color: '#7a4d0b',
                          borderColor: 'rgba(151,113,39,0.4)',
                          '&:hover': { borderColor: '#9a6a1f', bgcolor: 'rgba(151,113,39,0.08)' }
                        }}
                      >
                        Görselleri yönet
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={headers.length} sx={{ borderBottom: 0, height: 280, p: 0 }}>
                  <Box
                    sx={{
                      height: 280,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      px: 2,
                      background: 'radial-gradient(circle at center, rgba(151,113,39,0.09) 0%, rgba(255,255,255,0.45) 48%, transparent 78%)'
                    }}
                  >
                    <Box sx={{ border: '1px solid rgba(151,113,39,0.18)', borderRadius: 2, px: 3, py: 2, bgcolor: 'rgba(255,255,255,0.72)' }}>
                      <Box sx={{ color: '#2f2516', fontSize: '1.05rem', fontWeight: 900, mb: 0.5 }}>
                        Henüz kampanya oluşturulmadı.
                      </Box>
                      <Box sx={{ color: '#5f5140', fontSize: '0.92rem' }}>
                        Yeni kampanya planlamak için “Kampanya Oluştur” butonuna basın.
                      </Box>
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <style>{`
        @media (max-width: 600px) {
          .ky-table-root {
            min-height: 240px !important;
            overflow: visible !important;
          }

          .ky-table-wrap {
            overflow: visible !important;
          }

          .ky-table {
            display: block !important;
            min-width: 0 !important;
            width: 100% !important;
          }

          .ky-table thead {
            display: none !important;
          }

          .ky-table tbody {
            display: block !important;
            width: 100% !important;
          }

          .ky-table tbody tr.ky-data-row,
          .ky-table tbody tr.ky-gap-row {
            display: grid !important;
            grid-template-columns: 1fr;
            grid-template-rows: auto auto auto;
            gap: 6px;
            padding: 10px 10px !important;
            border-bottom: 1px solid rgba(151,113,39,0.14) !important;
            height: auto !important;
          }

          .ky-table tbody tr.ky-gap-row {
            grid-template-rows: auto auto;
          }

          .ky-table tbody tr .MuiTableCell-root {
            display: block !important;
            padding: 0 !important;
            border: none !important;
            height: auto !important;
            max-width: none !important;
            white-space: normal !important;
            overflow: visible !important;
            text-overflow: unset !important;
          }

          .ky-cell-name {
            grid-row: 1 !important;
          }

          .ky-cell-name .MuiTypography-root {
            white-space: normal !important;
            word-break: break-word !important;
          }

          .ky-cell-date {
            grid-row: 2 !important;
            font-size: 0.82rem !important;
          }

          .ky-cell-visuals {
            grid-row: 3 !important;
          }

          .ky-gap-row .ky-cell-visuals {
            display: none !important;
          }

          /* Dugme satir boyunca uzuyordu; icerigi kadar yer kaplasin */
          .ky-visual-btn {
            width: auto !important;
            justify-content: center !important;
            min-height: 34px !important;
          }
        }
      `}</style>
    </Box>
  );
}

export default CampaignTable;
